import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, X, FileText, ArrowRight } from "lucide-react";

const FALLBACK_CATEGORIES = [
  "Apparel & Textile",
  "Beauty & Cosmetics",
  "Skincare",
  "Lip Care",
  "Food & Beverage",
  "FMCG",
  "Home & Lifestyle",
  "Fitness & Wellness",
  "Corporate Gifting",
  "Consumer Electronics",
  "Nutraceuticals",
  "Jewellery",
  "Other",
];

const BUDGET_RANGES = [
  "₹50,000 – ₹1,00,000",
  "₹1,00,000 – ₹5,00,000",
  "₹5,00,000 – ₹15,00,000",
  "₹15,00,000 – ₹50,00,000",
  "₹50,00,000+"
];

const TIMELINE_OPTIONS = [
  "1–4 weeks (urgent — discuss with team)",
  "1–2 months",
  "2–4 months",
  "4+ months / flexible"
];
const DEFAULT_SKU_COLUMNS = [
  { key: "variant", label: "Variant", type: "text" },
  { key: "unit", label: "Unit", type: "text" },
  { key: "target_price", label: "Target price", type: "text" },
  { key: "notes", label: "Notes", type: "text" },
];

const BUDGET_FROM_RANGE = {
  "₹1–5 L": 300000, "₹5–15 L": 1000000, "₹15–50 L": 3000000, "₹50 L+": 7500000,
  "₹50,000 – ₹1,00,000": 75000,
  "₹1,00,000 – ₹5,00,000": 300000,
  "₹5,00,000 – ₹15,00,000": 1000000,
  "₹15,00,000 – ₹50,00,000": 3000000,
  "₹50,00,000+": 7500000,
};

const emptySkuRow = () => ({
  sku: "",
  product_name: "",
  variant: "",
  quantity: "",
  unit: "pcs",
  target_price: "",
  notes: "",
});

export default function NewRFQ() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [availableCategories, setAvailableCategories] = useState(FALLBACK_CATEGORIES);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const initialCategory = params.get("category") || FALLBACK_CATEGORIES[0];
  let answers = {};
  try { answers = JSON.parse(sessionStorage.getItem("askwinn_funnel_answers") || "{}"); } catch {}
  const prefillBudget = BUDGET_FROM_RANGE[answers.budget_range] || 50000;
  const prefillTimeline = answers.timeline || "8 weeks";

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    title: "",
    description: answers.product_idea || "",
    category: initialCategory,
    quantity: 100,
    budget_range: answers.budget_range || BUDGET_RANGES[1],
    timeline: answers.timeline || TIMELINE_OPTIONS[1],
  });
  const [requirements, setRequirements] = useState({});
  const [requirementsFormat, setRequirementsFormat] = useState("structured"); // "structured" or "text"
  const [requirementsText, setRequirementsText] = useState("");
  const [schema, setSchema] = useState(null);
  const [skuRows, setSkuRows] = useState([emptySkuRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [policyConsent, setPolicyConsent] = useState(false);
  const [createdRfq, setCreatedRfq] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const totalSkuQuantity = skuRows.reduce((total, row) => total + (Number(row.quantity) || 0), 0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    // Rule #2/#11: the RFQ category must come from the standardized
    // Sector/Category master, never a free-text or ad-hoc list.
    axios.get(`${API}/sectors`)
      .then((r) => {
        if (cancelled) return;
        const categories = Array.isArray(r.data) && r.data.length ? r.data.map((s) => s.name) : FALLBACK_CATEGORIES;
        setAvailableCategories(categories);
        setForm((current) => (categories.includes(current.category) ? current : { ...current, category: categories[0] || FALLBACK_CATEGORIES[0] }));
      })
      .catch(() => {
        if (!cancelled) setAvailableCategories(FALLBACK_CATEGORIES);
      });
    axios.get(`${API}/templates`)
      .then((r) => { if (!cancelled) setTemplates(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Category is selected first and is the authoritative filter for templates.
  const categoryTemplates = templates.filter((template) => (template.category || template.sector) === form.category);
  const selectedTemplate = categoryTemplates.find((t) => t.template_id === templateId) || null;
  const visibleSkuColumns = (selectedTemplate?.sku_columns?.length ? selectedTemplate.sku_columns : DEFAULT_SKU_COLUMNS).map((column) => typeof column === "string" ? { key: column, label: column.replaceAll("_", " "), type: "text" } : column);
  useEffect(() => {
    if (!selectedTemplate?.sku_columns?.length) return;
    setSkuRows((rows) => rows.map((row) => visibleSkuColumns.reduce((next, column) => ({ ...next, [column.key]: next[column.key] ?? "" }), row)));
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (templateId && !selectedTemplate) {
      setTemplateId("");
      setSchema(null);
      setRequirements({});
    } else if (selectedTemplate) {
      setSchema({ label: selectedTemplate.name, fields: selectedTemplate.fields || [] });
      setRequirements({});
    }
  }, [templateId, form.category, selectedTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load schema when category changes (only when no template is selected —
  // a template's own fields take precedence once chosen).
  useEffect(() => {
    if (selectedTemplate) return;
    if (!form.category) return;
    axios.get(`${API}/rfqs/categories/${encodeURIComponent(form.category)}/schema`)
      .then((r) => {
        setSchema(r.data?.fields?.length ? r.data : null);
        setRequirements({});
      })
      .catch(() => {
        setSchema(null);
        setRequirements({});
      });
  }, [form.category, templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setReq = (k, v) => setRequirements((r) => ({ ...r, [k]: v }));
  const toggleReq = (k, v) => setRequirements((r) => {
    const cur = r[k] || [];
    return { ...r, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });

  const updateSkuRow = (index, key, value) => {
    setSkuRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  };

  const addSkuRow = () => setSkuRows((current) => [...current, emptySkuRow()]);

  const removeSkuRow = (index) => {
    setSkuRows((current) => current.length === 1 ? [emptySkuRow()] : current.filter((_, rowIndex) => rowIndex !== index));
  };

  const submitRfq = async (e) => {
    e.preventDefault();
    if (totalSkuQuantity < 1) {
      toast.error("Add at least one SKU with a quantity before posting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        template_id: templateId || undefined,
        quantity: totalSkuQuantity,
        budget_usd: BUDGET_FROM_RANGE[form.budget_range] || 300000,
        policy_consent: policyConsent,
      };
      
      const normalizedSkus = skuRows
        .filter((row) => row.sku || row.product_name || row.quantity || visibleSkuColumns.some((column) => row[column.key]))
        .map((row) => ({
          sku: row.sku || "",
          product_name: row.product_name || "",
          quantity: row.quantity ? Number(row.quantity) : 0,
          ...visibleSkuColumns.reduce((values, column) => ({ ...values, [column.key]: row[column.key] || "" }), {}),
        }));

      // Snapshot the exact SKU columns shown at submission time so the RFQ
      // detail view can render them for manufacturers without needing to
      // re-fetch (or be affected by later edits to) the template.
      payload.sku_columns = visibleSkuColumns.map((column) => ({
        key: column.key,
        label: column.label,
        type: column.type || "text",
        options: column.options || [],
        required: Boolean(column.required),
      }));

      // Handle requirements based on format
      if (requirementsFormat === "text") {
        payload.requirements_text = requirementsText;
        payload.requirements = { ...requirements, skus: normalizedSkus };
      } else {
        payload.requirements = { ...requirements, skus: normalizedSkus };
        payload.requirements_text = "";
      }
      payload.skus = normalizedSkus;

      const r = await axios.post(`${API}/rfqs`, payload);
      setCreatedRfq(r.data);
      setStep(3);
      toast.success("RFQ created. Add attachments next.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onFileChange = async (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (!newFiles.length || !createdRfq) return;
    setUploading(true);
    for (const f of newFiles) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await axios.post(`${API}/rfqs/${createdRfq.rfq_id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, r.data]);
        toast.success(`Uploaded ${f.name}`);
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = async (file_id) => {
    await axios.delete(`${API}/rfqs/${createdRfq.rfq_id}/attachments/${file_id}`);
    setFiles((p) => p.filter((f) => f.file_id !== file_id));
  };

  const finalize = () => nav(`/rfqs/${createdRfq.rfq_id}`);

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline mb-4">§ NEW BRIEF · STEP {step} OF 3</div>
        <h1 className="font-serif text-5xl lg:text-6xl font-light leading-none tracking-tight mb-4">Post an <em className="text-klein not-italic">RFQ</em>.</h1>
        <div className="flex gap-2 mb-10 mt-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1 flex-1 ${n <= step ? "bg-klein" : "bg-[--border-soft]"}`} />
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(2); }} className="space-y-8" data-testid="rfq-form-step1">
            <Field label="Brand name">
              <input required className="input-underline" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Your brand name" data-testid="rfq-brand-name" />
            </Field>
            <Field label="Description">
              <textarea required className="input-underline min-h-[120px]" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Brief overview, vision, what makes this product special…" data-testid="rfq-description" />
            </Field>
            <Field label="Category">
              <select className="input-underline" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="rfq-category">
                {availableCategories.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            {templates.length > 0 && (
              <Field label={`Requirement template${categoryTemplates.length ? "" : " (none available for this category)"}`}>
                <select className="input-underline" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="rfq-template">
                  <option value="">— no template —</option>
                  {categoryTemplates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                </select>
              </Field>
            )}
            <div className="grid md:grid-cols-2 gap-8">
              <Field label="Budget range">
                <select className="input-underline" value={form.budget_range} onChange={(e) => set("budget_range", e.target.value)} data-testid="rfq-budget">
                  {BUDGET_RANGES.map((b) => <option key={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Timeline">
                <select className="input-underline" value={form.timeline} onChange={(e) => set("timeline", e.target.value)} data-testid="rfq-timeline">
                  {TIMELINE_OPTIONS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex gap-4 pt-6">
              <button type="submit" className="btn-primary" data-testid="rfq-next-1">
                {schema ? "Next: detailed brief" : "Next: review details"} <ArrowRight className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => nav("/rfqs")} className="btn-outline">Cancel</button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitRfq} className="space-y-6" data-testid="rfq-form-step2">
            {schema && (
              <div className="mb-6">
                <div className="overline mb-3">REQUIREMENTS FORMAT</div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setRequirementsFormat("structured")}
                    className={`px-4 py-2 border ${requirementsFormat === "structured" ? "border-klein bg-klein/5 text-klein" : "border-[--border-soft]"}`}
                  >
                    Structured table
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequirementsFormat("text")}
                    className={`px-4 py-2 border ${requirementsFormat === "text" ? "border-klein bg-klein/5 text-klein" : "border-[--border-soft]"}`}
                  >
                    Free-form text
                  </button>
                </div>
              </div>
            )}
            
            {requirementsFormat === "structured" ? (
              <div className="space-y-6">
                {schema && (
                  <>
                    <div className="overline">{schema.label?.toUpperCase()}</div>
                    <p className="text-sm text-[--muted-foreground] mb-4">Specific spec questions help us match you to the right factory faster.</p>
                  </>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[820px] border border-[--border-soft] bg-white/40">
                    <thead>
                      <tr className="border-b border-[--border-soft] text-left text-[10px] uppercase tracking-[0.2em] text-[--muted-foreground]">
                        <th className="p-3">SKU</th>
                        <th className="p-3">Product name</th>
                        {visibleSkuColumns.map((column) => <th key={column.key} className="p-3">{column.label}</th>)}
                        <th className="p-3">Qty</th>
                        <th className="p-3 w-14" />
                      </tr>
                    </thead>
                    <tbody>
                      {skuRows.map((row, index) => (
                        <tr key={`${index}-${row.sku || "new"}`} className="border-b border-[--border-soft] align-top">
                          <td className="p-2"><input value={row.sku} onChange={(e) => updateSkuRow(index, "sku", e.target.value)} className="input-underline text-sm" placeholder="SKU-01" /></td>
                          <td className="p-2"><input required value={row.product_name} onChange={(e) => updateSkuRow(index, "product_name", e.target.value)} className="input-underline text-sm" placeholder="e.g. Vitamin C Serum" /></td>
                          {visibleSkuColumns.map((column) => <td key={column.key} className="p-2">{column.type === "select" ? <select required={column.required} value={row[column.key] || ""} onChange={(e) => updateSkuRow(index, column.key, e.target.value)} className="input-underline text-sm"><option value="">Select</option>{(column.options || []).map((option) => <option key={option}>{option}</option>)}</select> : <input required={column.required} type={column.type === "number" ? "number" : "text"} value={row[column.key] || ""} onChange={(e) => updateSkuRow(index, column.key, e.target.value)} className="input-underline text-sm" placeholder={column.label} />}</td>)}
                          <td className="p-2"><input type="number" min="0" value={row.quantity} onChange={(e) => updateSkuRow(index, "quantity", e.target.value)} className="input-underline text-sm" placeholder="100" /></td>
                          <td className="p-2"><button type="button" className="text-xs text-[--muted-foreground]" onClick={() => removeSkuRow(index)}>Remove</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button type="button" onClick={addSkuRow} className="btn-outline text-xs">+ Add SKU row</button>
                <div className="flex items-center justify-between border-t border-[--border-soft] pt-4 text-sm">
                  <span className="text-[--muted-foreground]">Total quantity from SKUs</span>
                  <strong data-testid="rfq-total-qty">{totalSkuQuantity.toLocaleString()}</strong>
                </div>

                {!selectedTemplate && schema && schema.fields.filter((f) => !["sku", "product_name", "quantity"].includes(f.key)).map((f) => (
                  <div key={f.key} data-testid={`req-${f.key}`}>
                    <div className="overline mb-2">{f.label}</div>
                    {f.type === "select" && (
                      <select className="input-underline" value={requirements[f.key] || ""} onChange={(e) => setReq(f.key, e.target.value)}>
                        <option value="">— select —</option>
                        {f.options.map((o) => <option key={o}>{o}</option>)}
                      </select>
                    )}
                    {f.type === "text" && (
                      <input className="input-underline" value={requirements[f.key] || ""} onChange={(e) => setReq(f.key, e.target.value)} placeholder={f.placeholder} />
                    )}
                    {f.type === "multi" && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {f.options.map((o) => (
                          <button type="button" key={o} onClick={() => toggleReq(f.key, o)}
                            className={`tag ${(requirements[f.key] || []).includes(o) ? "verified" : ""} cursor-pointer`}>
                            {o}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : requirementsFormat === "text" ? (
              <>
                <div className="overline">DETAILED REQUIREMENTS (FREE-FORM)</div>
                <p className="text-sm text-[--muted-foreground] mb-4">Describe all your requirements in detail. Manufacturers will review and indicate which they can fulfill.</p>
                <textarea
                  className="input-underline min-h-[200px]"
                  value={requirementsText}
                  onChange={(e) => setRequirementsText(e.target.value)}
                  placeholder="Example:&#10;- Material: 100% organic cotton&#10;- Certification: GOTS required&#10;- Packaging: Biodegradable poly bags&#10;- Labeling: Custom woven labels&#10;- Timeline: 45 days to delivery"
                />
              </>
            ) : (
              <div className="editorial-card p-6">
                <p className="text-sm text-[--muted-foreground]">No structured form for this category yet. Click submit to continue with the basics + attachments.</p>
              </div>
            )}
            <label className="flex items-start gap-3 border-t border-[--border-soft] pt-5 text-sm leading-relaxed text-[--muted-foreground]">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[--klein]" checked={policyConsent} onChange={(event) => setPolicyConsent(event.target.checked)} required />
              <span>I agree to AskWinn's RFQ terms and privacy practices. Manufacturer identities and contact details stay hidden until the manufacturer-details access payment is verified.</span>
            </label>
            <p className="text-sm text-[--muted-foreground]" data-testid="rfq-access-fee-copy">
              Manufacturer-details access is priced at ₹2,000 per RFQ after posting; payment collection is not enabled in this release. Sample costs, if any, are quoted by the manufacturer. AskWinn does not charge a separate sample fee.
            </p>
            <div className="flex gap-4 pt-6">
              <button type="button" onClick={() => setStep(1)} className="btn-outline">Back</button>
              <button type="submit" disabled={submitting || !policyConsent} className="btn-primary" data-testid="rfq-submit">
                {submitting ? "Posting…" : "Post RFQ →"}
              </button>
            </div>
          </form>
        )}

        {step === 3 && createdRfq && (
          <div className="space-y-6" data-testid="rfq-form-step3">
            <div className="editorial-card p-6 border-l-4 border-l-klein">
              <div className="overline mb-2">✓ RFQ POSTED</div>
              <p className="text-sm">Now attach tech packs, mood boards or spec sheets — manufacturers will see them when reviewing your brief.</p>
            </div>

            <label className="editorial-card p-10 border-dashed border-2 border-[--border-soft] hover:border-klein flex flex-col items-center cursor-pointer transition-colors" data-testid="rfq-upload-zone">
              <Upload className="w-8 h-8 text-klein mb-3" />
              <div className="font-serif text-2xl mb-1">Drop files or click to select</div>
              <div className="overline text-[10px]">PDF · PNG · JPG · XLSX · DOCX · MAX 20MB · UP TO 10 FILES</div>
              <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.xlsx,.docx" onChange={onFileChange} className="hidden" data-testid="rfq-file-input" />
            </label>

            {uploading && <div className="font-mono text-xs text-[--muted-foreground]">UPLOADING…</div>}

            <div className="space-y-2" data-testid="rfq-attachments-list">
              {files.map((f) => (
                <div key={f.file_id} className="editorial-card p-4 flex items-center justify-between" data-testid={`attachment-${f.file_id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-klein" />
                    <div>
                      <div className="font-serif text-lg">{f.filename}</div>
                      <div className="font-mono text-[10px] text-[--muted-foreground]">{(f.size / 1024).toFixed(0)} KB</div>
                    </div>
                  </div>
                  <button onClick={() => removeFile(f.file_id)} className="p-2 hover:bg-[--muted]" data-testid={`remove-${f.file_id}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-4 pt-6">
              <button onClick={finalize} className="btn-primary" data-testid="rfq-finish">
                Finish & view RFQ <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <div className="overline mb-1">{label}</div>
    {children}
  </div>
);
